import axios, { type AxiosInstance } from "axios";
import { useAuthStore } from "../store/authStore.js";

/**
 * 共享 axios 实例：自动注入 Authorization + X-Install-Id；401 时清空 session 触发重新 handshake。
 */
function createHttp(): AxiosInstance {
  const instance = axios.create({
    baseURL: "/api",
    timeout: 20_000,
  });

  instance.interceptors.request.use((cfg) => {
    const { sessionToken, setupToken, installId } = useAuthStore.getState();
    cfg.headers = cfg.headers ?? {};
    // setup_token 用于 /installations 首装 + /installations/test-pat-setup 首装阶段测试 PAT
    const isSetupCreate = cfg.url === "/installations" && cfg.method?.toLowerCase() === "post";
    const isSetupTestPat = cfg.url === "/installations/test-pat-setup" && cfg.method?.toLowerCase() === "post";
    if ((isSetupCreate || isSetupTestPat) && setupToken) {
      cfg.headers["Authorization"] = `Bearer ${setupToken}`;
    } else if (sessionToken && cfg.url !== "/auth/handshake") {
      cfg.headers["Authorization"] = `Bearer ${sessionToken}`;
      if (installId) cfg.headers["X-Install-Id"] = installId;
    }
    return cfg;
  });

  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err?.response?.status === 401) {
        useAuthStore.getState().clearSession();
      }
      return Promise.reject(err);
    },
  );

  return instance;
}

export const http = createHttp();

export interface ApiError {
  code: string;
  message: string;
  hint?: string;
}

export function parseApiError(err: unknown): ApiError {
  const e = err as { response?: { data?: { error?: ApiError } }; message?: string };
  if (e.response?.data?.error) return e.response.data.error;
  return { code: "unknown", message: e.message ?? "unknown error" };
}
